import React, { useMemo } from 'react';
import Button from '@/components/UI/Button';
import DeleteButton from '@/components/UI/DeleteButton';
import EditButton from '@/components/UI/EditButton';
import EmptyState from '@/components/UI/EmptyState';
import type { GovernanceFamilyLimitDraft } from './governanceHelpers';

export interface GovernanceFamilyOption {
    familyId: number;
    name: string;
}

interface GovernanceFamilyLimitsTableProps {
    familyLimits: GovernanceFamilyLimitDraft[];
    families: GovernanceFamilyOption[];
    onAdd: () => void;
    onEdit: (familyId: number) => void;
    onDelete: (familyId: number) => void;
}

const formatCap = (value: number | null): string => (value === null ? 'No cap' : value.toString());

/** The editable per-strategy-family limit rows of a governance policy draft, rendered as a compact table. */
const GovernanceFamilyLimitsTable: React.FC<GovernanceFamilyLimitsTableProps> = ({ familyLimits, families, onAdd, onEdit, onDelete }) => {
    const familyNameById = useMemo(() => new Map(families.map((family) => [family.familyId, family.name])), [families]);
    const canAdd = families.length > 0 && familyLimits.length < families.length;

    return (
        <div className="border-t border-zinc-800 pt-4">
            <div className="mb-2 flex items-center justify-between">
                <div>
                    <h6 className="text-base font-semibold">Per-family limits</h6>
                    <p className="text-sm text-zinc-500">Aggregate caps across all live variants in a strategy family.</p>
                </div>
                <Button disabled={!canAdd} variant="link" onClick={onAdd}>
                    + Add
                </Button>
            </div>

            {familyLimits.length === 0 ? (
                <EmptyState size="sm" title="No per-family limits configured." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse bg-zinc-800 text-white">
                        <thead>
                            <tr className="bg-zinc-700 text-left">
                                <th className="px-3 py-2">Family</th>
                                <th className="px-3 py-2">Max positions</th>
                                <th className="px-3 py-2">Max risk %</th>
                                <th className="px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {familyLimits.map((limit, index) => (
                                <tr className={index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'} key={limit.familyId}>
                                    <td className="border-t border-zinc-700 px-3 py-2">
                                        {familyNameById.get(limit.familyId) ?? `Family #${limit.familyId}`}
                                    </td>
                                    <td className="border-t border-zinc-700 px-3 py-2">{formatCap(limit.maxOpenPositionsPerFamily)}</td>
                                    <td className="border-t border-zinc-700 px-3 py-2">
                                        {limit.maxFamilyRiskCapitalPercent === null ? 'No cap' : `${limit.maxFamilyRiskCapitalPercent}%`}
                                    </td>
                                    <td className="border-t border-zinc-700 px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton size="sm" onClick={() => onEdit(limit.familyId)} />
                                            <DeleteButton size="sm" onClick={() => onDelete(limit.familyId)} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default GovernanceFamilyLimitsTable;
